/**
 * Composable auth (login & profile).
 *
 * Login adalah mutation. Setelah login sukses:
 *   - Token disimpan di localStorage `token-auth-jaskipin`
 *   - Profile diisi di localStorage `data-profile-jaskipin`
 *   - Cache di-clear total (queryClient.clear()) untuk reset state user lama
 *
 * Profile adalah query — cache 5 menit.
 */
import { useQuery, useMutation, useQueryClient } from "@tanstack/vue-query";
import { loginUser, fetchProfile } from "@/queries/api/auth";
import { clearPersistedCache } from "@/config/queryClient";

const TOKEN_KEY = "token-auth-jaskipin";
const PROFILE_KEY = "data-profile-jaskipin";
const PROFILE_QUERY_KEY = ["auth", "profile"];

function persistAuth(response) {
  /* Format response project ini (existing):
       { status, message, data: { token, ... }, profile: {...} } — bervariasi.
     Komponen yang panggil login bertanggung jawab menyimpan ke localStorage
     bila format khusus. Helper di sini hanya menyimpan kalau ada `token`. */
  try {
    if (response && response.token) {
      window.localStorage.setItem(
        TOKEN_KEY,
        JSON.stringify({ token: response.token })
      );
    }
    if (response && response.data && response.data.token) {
      window.localStorage.setItem(
        TOKEN_KEY,
        JSON.stringify({ token: response.data.token })
      );
    }
    if (response && response.profile) {
      window.localStorage.setItem(
        PROFILE_KEY,
        JSON.stringify(response.profile)
      );
    }
  } catch (_) {
    /* localStorage quota / disabled */
  }
}

export function useLogin(options = {}) {
  const qc = useQueryClient();
  /* Pisahkan onSuccess user-supplied agar tidak ditimpa oleh spread di bawah. */
  const userOnSuccess = options.onSuccess;
  const restOptions = { ...options };
  delete restOptions.onSuccess;
  return useMutation({
    mutationFn: loginUser,
    onSuccess: (response, variables, context) => {
      persistAuth(response);
      qc.clear();
      clearPersistedCache();
      if (userOnSuccess) userOnSuccess(response, variables, context);
    },
    ...restOptions,
  });
}

export function useProfile(options = {}) {
  return useQuery({
    queryKey: PROFILE_QUERY_KEY,
    queryFn: fetchProfile,
    staleTime: 5 * 60 * 1000,
    retry: 0,
    ...options,
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return () => {
    try {
      window.localStorage.removeItem(TOKEN_KEY);
      window.localStorage.removeItem(PROFILE_KEY);
    } catch (_) {
      /* ignore */
    }
    qc.clear();
    clearPersistedCache();
  };
}
